import React, { useState, useMemo } from "react";
import { Upload, X, Plus, Loader2, CheckCircle2, Calendar, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCampaignDays } from "../../hooks/useCampaignDays";
import { getLocationFileInputId } from "../../utils/domIds";
import { format, addDays, parseISO } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Creative } from "../../types";

interface DayBasedUploadProps {
  locationName: string;
  creatives: Creative[];
  onFileChange: (locationName: string, e: React.ChangeEvent<HTMLInputElement>, day?: number) => void;
  onRemoveCreative: (locationName: string, index: number) => void;
  onUpdateCreativeDays?: (locationName: string, creativeIndex: number, days: number[]) => void;
  isUploading: boolean;
}

const DayBasedUpload = ({
  locationName,
  creatives,
  onFileChange,
  onRemoveCreative,
  onUpdateCreativeDays,
  isUploading,
}: DayBasedUploadProps) => {
  const { t, language } = useLanguage();
  const { totalDays } = useCampaignDays();
  
  // Get campaign start date from localStorage
  const { startDate, campaignDates } = useMemo(() => {
    const storedStartDate = localStorage.getItem("startDate");
    const start = storedStartDate ? parseISO(storedStartDate) : new Date();
    
    // Generate all campaign dates
    const dates: Date[] = [];
    for (let i = 0; i < totalDays; i++) {
      dates.push(addDays(start, i));
    }
    
    return { startDate: start, campaignDates: dates };
  }, [totalDays]);

  // Format date for display
  const formatDateDisplay = (date: Date) => {
    const locale = language === 'ar' ? ar : enUS;
    return format(date, 'EEE, MMM d', { locale });
  };

  const formatDateShort = (date: Date) => {
    const locale = language === 'ar' ? ar : enUS;
    return format(date, 'MMM d', { locale });
  };

  // Get the date for a specific day number (1-indexed)
  const getDateForDay = (day: number): Date => {
    return campaignDates[day - 1] || addDays(startDate, day - 1);
  };

  // Handle changing the scheduled day for a creative
  const handleDayChange = (creativeIndex: number, newDay: string) => {
    if (onUpdateCreativeDays) {
      onUpdateCreativeDays(locationName, creativeIndex, [parseInt(newDay, 10)]);
    }
  };

  // Simple file input for initial upload
  const fileInputId = getLocationFileInputId(locationName);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Do NOT set a default day - user must choose after upload when there are 2+ creatives
    onFileChange(locationName, e);
  };

  // Only show scheduling UI when there are 2+ creatives
  const showSchedulingUI = creatives.length >= 2;

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      {isUploading ? (
        <div className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-border rounded-lg">
          <Loader2 className="h-6 w-6 text-primary animate-spin mb-2" />
          <p className="text-xs text-muted-foreground">
            {language === 'ar' ? 'جاري التحميل...' : 'Uploading...'}
          </p>
        </div>
      ) : (
        <label
          htmlFor={fileInputId}
          className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
        >
          <Upload className="h-6 w-6 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {creatives.length > 0 
              ? (language === 'ar' ? '+ إضافة المزيد من الملفات' : '+ Add more files')
              : (language === 'ar' ? 'انقر لتحميل ملفات' : 'Click to upload files')
            }
          </p>
          <p className="text-xs text-primary font-medium mt-1">
            📁 {language === 'ar' ? 'ملفات متعددة مدعومة' : 'Multiple files supported'}
          </p>
          <Input
            type="file"
            id={fileInputId}
            onChange={handleFileInputChange}
            accept=".jpg,.jpeg,.png,.gif,.mp4,.mpeg,.3gp,.avi,.mov"
            multiple={true}
            className="hidden"
            disabled={isUploading}
          />
        </label>
      )}

      {/* Uploaded Files List with Day Selector */}
      {creatives.length > 0 && (
        <div className="space-y-2">
          <h6 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            {showSchedulingUI 
              ? (language === 'ar' ? 'الملفات المرفوعة - حدد يوم العرض:' : 'Uploaded Files - Select display date:')
              : (language === 'ar' ? 'الملفات المرفوعة:' : 'Uploaded Files:')
            }
          </h6>
          
          {creatives.map((creative, index) => {
            const scheduledDay = creative.scheduledDays?.[0];
            const scheduledDate = scheduledDay ? getDateForDay(scheduledDay) : null;
            const hasNoDay = !scheduledDay;
            
            return (
              <div 
                key={index} 
                className="flex items-center gap-2 bg-background p-3 rounded-lg border border-border"
              >
                {/* File info */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${hasNoDay && showSchedulingUI ? 'bg-primary/20' : 'bg-green-100'}`}>
                    {hasNoDay && showSchedulingUI ? (
                      <Calendar className="h-4 w-4 text-primary" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <span className="text-sm text-foreground truncate" title={creative.name}>
                    {creative.name}
                  </span>
                </div>

                 {/* Day Selector - only show when 2+ creatives exist */}
                 {totalDays > 1 && showSchedulingUI && onUpdateCreativeDays && (
                   <Select
                     value={scheduledDay?.toString() || ""}
                     onValueChange={(value) => handleDayChange(index, value)}
                   >
                     <SelectTrigger className={`w-[140px] h-8 text-xs bg-background ${hasNoDay ? 'border-primary/50' : ''}`}>
                       <SelectValue placeholder={language === 'ar' ? 'اختر يوم' : 'Select day'}>
                         {scheduledDate ? formatDateShort(scheduledDate) : (language === 'ar' ? 'اختر يوم' : 'Select day')}
                       </SelectValue>
                     </SelectTrigger>
                     <SelectContent className="z-50 bg-popover border shadow-md">
                       {campaignDates.map((date, dayIdx) => (
                         <SelectItem key={dayIdx} value={(dayIdx + 1).toString()} className="cursor-pointer">
                           <div className="flex flex-col">
                             <span>{formatDateDisplay(date)}</span>
                             <span className="text-xs text-muted-foreground">
                               {language === 'ar' ? `اليوم ${dayIdx + 1}` : `Day ${dayIdx + 1}`}
                             </span>
                           </div>
                         </SelectItem>
                       ))}
                     </SelectContent>
                   </Select>
                 )}

                {/* Remove button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveCreative(locationName, index)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                  aria-label={t("remove")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Info text showing campaign date range */}
      {totalDays > 1 && (
        <p className="text-xs text-muted-foreground text-center">
          {language === 'ar'
            ? `حملتك من ${formatDateDisplay(campaignDates[0])} إلى ${formatDateDisplay(campaignDates[totalDays - 1])} (${totalDays} أيام)`
            : `Campaign: ${formatDateDisplay(campaignDates[0])} - ${formatDateDisplay(campaignDates[totalDays - 1])} (${totalDays} days)`}
        </p>
      )}
    </div>
  );
};

export default DayBasedUpload;
